import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { TabType } from '../common/tab-scroller/tab-scroller.component';
import { JobScreenCommonService } from '../common/job-screen-common.service';
import { BBWTMessageService, EventService, JobsService, UserService } from '../../../services';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import {
  IGrabRouteRecommendationResultFailure,
  IRouteRecommendation,
} from '../../../interfaces/route-recommendations/route-recommendation';
import { RouteRecommendationService } from '../../../services/route-recommendation.service';
import {
  IRouteHiddenReasonSelection,
  ISetRouteHidden,
} from '../../../interfaces/route-recommendations/route-rejection-reason';
import { ConfirmationService } from 'primeng';
import { OrganisationTypeEnum } from '../../../enums/organisation-types';
import { RoutesAnalyticsService } from 'src/app/services/product-analytics/routes-analytics.service';

@Component({
  selector: 'job-screen-route-details',
  templateUrl: './route-details.component.html',
  styleUrls: ['./route-details.component.scss', '../common/job-screen-common.scss'],
})
export class JobScreenRouteDetailsComponent implements OnInit, OnDestroy {
  @Input('organisationId') organisationId!: number;

  routeId: number;
  routeRecommendation: IRouteRecommendation = null;
  subscriptions: Subscription[] = [];

  tabs: TabType[] = [];
  public activeViewId: string = 'routeItinerary';

  public grabbingRoute = false;
  public showRejectionReasonDialog = false;
  public showConfirmationPopup = false;
  public noLongerAvailable = false;

  isVME: boolean = false;

  get grabBonusCount(): number {
    return this.routeRecommendation?.grabBonus?.bonusCount ?? 0;
  }

  constructor(
    public commonService: JobScreenCommonService,
    private routeRecommendationService: RouteRecommendationService,
    private route: ActivatedRoute,
    private eventService: EventService,
    private location: Location,
    private confirmationService: ConfirmationService,
    private messageService: BBWTMessageService,
    private userService: UserService,
    private routeAnalyticService: RoutesAnalyticsService,
  ) {
    this.initSubscriptions();
  }

  ngOnInit(): void {
    this.routeId = parseInt(this.route.snapshot.paramMap.get('id'));

    this.tabs = [
      { label: 'Itinerary', id: 'routeItinerary', type: 'component' },
      { label: 'Route Overview', id: 'routeOverview', type: 'scroll' },
      { label: 'Task Details', id: 'taskDetails', type: 'scroll' },
    ];

    this.userService.getCurrentUserOrg().then((currentUserOrg) => {
      this.isVME = currentUserOrg.organisationTypeId === OrganisationTypeEnum.Engineius;
    });

    void this.getRouteRecommendation(this.routeId);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x?.unsubscribe());
  }

  initSubscriptions(): void {
    this.subscriptions.push(
      this.eventService.JobScreenEvent.subscribe((event: any) => {
        this.handleEvents(event);
      }),
    );
  }

  private handleEvents(event: any) {
    if (event.event == 'job-rejected') {
      if (this.routeId == event.data.jobId) {
        void this.getRouteRecommendation(this.routeId);
      }
    }

    if (event.event == 'refresh-job') {
      void this.getRouteRecommendation(this.routeId);
    }
  }

  async getRouteRecommendation(routeId: number): Promise<void> {
    const response = await this.routeRecommendationService.get(this.routeId);

    if (!response) this.noLongerAvailable = true;

    if (response.routeGrabbed && !this.showConfirmationPopup) {
      window.location.href = `/app/jobscreen/${response.jobId}`;
    }

    this.routeRecommendation = response;
  }

  goBack(): void {
    this.location.back();
  }

  onTabSelected(tab: TabType): void {
    if (tab.type === 'component') {
      this.activeViewId = tab.id;
    } else {
      this.activeViewId = 'scroll';
    }
  }

  sendTabAnalytic(tab: TabType) {
    if (tab.label == 'Task Details')
      this.routeAnalyticService.sendRouteTaskDetailsViewEvent(this.routeId);
    if (tab.label == 'Itinerary') {
      this.routeAnalyticService.sendRouteItineraryEvent(this.routeId);
    }
  }

  async watchRoute() {
    await this.routeRecommendationService.setWatched(this.routeRecommendation.id, true);
    this.routeRecommendation.watched = true;
  }

  async unwatchRoute() {
    await this.routeRecommendationService.setWatched(this.routeRecommendation.id, false);
    this.routeRecommendation.watched = false;
  }

  onConfirmHide(hideRouteSelection: IRouteHiddenReasonSelection) {
    const request: ISetRouteHidden = {
      routeHiddenComment: hideRouteSelection.routeHiddenComment,
      routeHiddenReasonId: hideRouteSelection.routeHiddenReasonId,
      hidden: true,
      routeRecommendationId: this.routeRecommendation.id,
    };

    this.routeRecommendationService.setHidden(request).then((x) => {
      void this.getRouteRecommendation(this.routeId);
      this.showRejectionReasonDialog = false;
    });
  }

  unHideRoute() {
    this.routeRecommendationService
      .setHidden({
        routeRecommendationId: this.routeRecommendation.id,
        hidden: false,
        routeHiddenReasonId: null,
        routeHiddenComment: null,
      })
      .then((x) => {
        void this.getRouteRecommendation(this.routeId);
      });
  }

  onGrabRouteClick() {
    this.routeAnalyticService.sendGrabNavigationEvent('Click grab from route details');
    this.confirmationService.confirm({
      message: `Grab route for £${this.routeRecommendation.supplierPrice}?`,
      key: this.routeRecommendation.id.toString(),
      acceptLabel: 'Grab',
      rejectVisible: false,
      accept: () => {
        void this.grabRoute();
      },
    });
  }

  private async grabRoute() {
    if (this.grabbingRoute) {
      return;
    }

    this.grabbingRoute = true;
    try {
      const result = await this.routeRecommendationService.grabRoute(
        this.routeRecommendation.id,
        [],
      );

      if (result.success) {
        this.messageService.ShowSuccess('Success', 'Route Grabbed');
        this.showConfirmationPopup = true;
        void this.getRouteRecommendation(this.routeId);
      } else if (result.failure == IGrabRouteRecommendationResultFailure.NoLongerAvailable) {
        this.messageService.ShowError('Failure', 'Route is no longer available.');
      } else {
        this.messageService.ShowError('Failure', 'A technical error occurred');
      }
    } catch (error) {
      this.messageService.ShowError('Failure', 'A technical error occurred');
    } finally {
      this.grabbingRoute = false;
    }
  }

  grabRouteLabel() {
    if (this.grabbingRoute) {
      return 'Please wait';
    } else if (this.routeRecommendation != null) {
      if (this.routeRecommendation.routeGrabbed == true) {
        return 'Your Route';
      } else if (this.routeRecommendation.invalidated) {
        return 'Unavailable';
      }
      return 'Grab Route';
    }
    return 'Loading..';
  }
}
